import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  KindEnum,
  NotFoundException,
  Pagination,
  Provider,
  TemplateEntity,
  WbSafeNunjucksApi,
  calculatedRef,
  catalogEntityAdapter,
  getLabelAndValue,
  parseDisplayField,
  selectTemplateEntity,
} from '@agilelab/plugin-wb-platform';
import { contextProviderFactory } from '../../utils';
import { useColumns } from '../../hooks';
import { useApi } from '@mainstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import { parseEntityRef } from '@mainstage/catalog-model';
import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import { GroupsFilters, GroupsFiltersSection } from './GroupFilters';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';

export type GroupTypeEntity = GenericEntityType & { ref: string };

interface GroupContextType
  extends GenericProviderType<GroupTypeEntity[], GroupsFilters> {}

const [useGroupContext, GroupContextProvider] =
  contextProviderFactory<GroupContextType>();

export default useGroupContext;

export interface GroupProviderProps {
  templateKind: TemplateEntity | undefined;
  children: React.ReactNode;
  parentTemplateRef?: string;
}

const defaultColumns = new Map<string, string>([
  ['name', '{{spec.profile.displayName}}'],
  [
    'description',
    '{% if spec.profile.email %}{{spec.profile.email}}{% else %}-{% endif %}',
  ],
  ['ref', '{{ref}}'],
]);

const toGroupTypeEntity = (group: any): GroupTypeEntity => {
  return {
    ...group,
    ref: expandEntityRef(calculatedRef(group)),

    __metadata: {
      kind: KindEnum.group,
      name: group.metadata.name,
    },
  };
};

export const GroupProvider: React.FC<GroupProviderProps> = props => {
  const { templateKind, children } = props;
  const catalogApi = useApi(catalogApiRef);
  const [filters, setFilters] = useState<GroupsFilters>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    currentPage: 0,
  });

  const columns: Column[] = useColumns(defaultColumns, templateKind);

  const groupsState = useAsync(async () => {
    const result = await catalogApi.queryEntities({
      limit: pagination.limit,
      cursor: pagination.cursor,
      fullTextFilter: {
        term: filters.search!,
        fields: ['spec.profile.displayName', 'metadata.name'],
      },
      filter: {
        kind: ['group'],
      },
    });
    setNextCursor(result.pageInfo.nextCursor);
    setPrevCursor(result.pageInfo.prevCursor);
    setCount(result.totalItems);
    return result.items.map(i => toGroupTypeEntity(i as any));
  }, [templateKind, filters, pagination.limit, pagination.cursor]);

  return (
    <GroupContextProvider
      value={{
        entitiesState: groupsState,
        columns,
        filters,
        changeFilters: <K extends keyof GroupsFilters>(
          key: K,
          filterValue: GroupsFilters[K],
        ) => {
          setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
          setFilters(f => ({ ...f, [key]: filterValue }));
        },
        resetFilters: () => {
          setFilters({});
        },
        templateKind,
        pagination,
        setPagination,
        count,
        nextCursor,
        prevCursor,
      }}
    >
      {children}
    </GroupContextProvider>
  );
};

export class GroupHandlerProvider
  implements Provider<GroupTypeEntity[], GroupsFilters>
{
  private readonly templateEntity: TemplateEntity | undefined;
  private readonly nunjucksApi: WbSafeNunjucksApi;
  private readonly catalogApi: CatalogApi;

  constructor(
    nunjucksApi: WbSafeNunjucksApi,
    catalogApi: CatalogApi,

    availableKinds: TemplateEntity[],
  ) {
    this.nunjucksApi = nunjucksApi;
    this.catalogApi = catalogApi;

    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.group);
  }

  useContext = useGroupContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: React.ReactNode,
    parentTemplateRef?: string,
  ) {
    return (
      <GroupProvider
        templateKind={templateKind}
        parentTemplateRef={parentTemplateRef}
      >
        {children}
      </GroupProvider>
    );
  }

  filtersSection = GroupsFiltersSection;

  adapt(entity: GenericEntityType) {
    const parsedDisplayField = parseDisplayField(
      this.nunjucksApi,
      entity,
      this.templateEntity?.displayField,
    );
    const catalogEntity = catalogEntityAdapter(
      entity,
      parsedDisplayField,
      true,
    );
    return catalogEntity;
  }

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) || ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'group') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity)
          return getLabelAndValue(
            toGroupTypeEntity(entity),
            this.adapt(toGroupTypeEntity(entity)),
            this.templateEntity,
          );

        throw new NotFoundException(
          `Cannot find a Group entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
